import { parseHttpUrl } from './http.js';
import { ConfigParseError, isRecord, parseInteger, parseOptionalText } from './types.js';
export const EXTERNAL_INTEGRATION_MODES = ['disabled', 'optional', 'required'];
function parseMode(source, key, defaultMode) {
    const value = parseOptionalText(source[key], key) ?? defaultMode;
    if (!EXTERNAL_INTEGRATION_MODES.includes(value)) {
        throw new ConfigParseError(key, 'must be disabled, optional, or required');
    }
    return value;
}
export function readExternalIntegrationMode(source, key, defaultMode = 'disabled') {
    return parseMode(source, key, defaultMode);
}
function placeholderUrl(value) {
    try {
        const hostname = new URL(value).hostname.toLowerCase();
        return hostname === 'invalid' || hostname.endsWith('.invalid');
    }
    catch {
        return false;
    }
}
export function assessExternalIntegration(source, definition) {
    const mode = parseMode(source, definition.modeKey, definition.defaultMode ?? 'disabled');
    if (mode === 'disabled') {
        return { id: definition.id, mode, available: false, reason: 'disabled' };
    }
    let missingConfiguration = false;
    for (const key of definition.requiredKeys) {
        try {
            if (parseOptionalText(source[key], key) === undefined)
                missingConfiguration = true;
        }
        catch {
            return { id: definition.id, mode, available: false, reason: 'invalid_configuration' };
        }
    }
    if (missingConfiguration) {
        return { id: definition.id, mode, available: false, reason: 'missing_configuration' };
    }
    for (const key of definition.urlKeys) {
        const value = source[key];
        try {
            parseHttpUrl(value, key);
        }
        catch {
            return { id: definition.id, mode, available: false, reason: 'invalid_configuration' };
        }
        if (placeholderUrl(value)) {
            return { id: definition.id, mode, available: false, reason: 'placeholder_configuration' };
        }
    }
    for (const field of definition.integerKeys ?? []) {
        try {
            parseInteger(source[field.key], field.key, field.minimum, field.maximum);
        }
        catch {
            return { id: definition.id, mode, available: false, reason: 'invalid_configuration' };
        }
    }
    return { id: definition.id, mode, available: true };
}
export function requiredExternalIntegrationsReady(statuses) {
    return statuses.every((status) => status.mode !== 'required' || status.available);
}
export function externalIntegrationUnavailableMessage(status) {
    switch (status.reason) {
        case 'disabled':
            return '该能力在当前部署中已关闭';
        case 'missing_configuration':
            return '当前部署尚未完成该能力配置';
        case 'invalid_configuration':
            return '当前部署的该能力配置无效';
        case 'placeholder_configuration':
            return '当前部署的该能力仍使用占位配置';
        case 'upstream_unavailable':
            return '该能力的上游服务当前不可用';
        default:
            return status.available ? '' : '该能力当前不可用';
    }
}
export class ExternalIntegrationUnavailableError extends Error {
    status;
    constructor(status) {
        super(externalIntegrationUnavailableMessage(status));
        this.name = 'ExternalIntegrationUnavailableError';
        this.status = status;
    }
}
export function readExternalIntegrationStatus(value, id) {
    if (!Array.isArray(value))
        return undefined;
    for (const item of value) {
        if (!isRecord(item) || item['id'] !== id)
            continue;
        const mode = item['mode'];
        const available = item['available'];
        const reason = item['reason'];
        if (!EXTERNAL_INTEGRATION_MODES.includes(mode) ||
            typeof available !== 'boolean') {
            return undefined;
        }
        if (reason !== undefined &&
            ![
                'disabled',
                'missing_configuration',
                'invalid_configuration',
                'placeholder_configuration',
                'upstream_unavailable',
            ].includes(String(reason))) {
            return undefined;
        }
        if (available) {
            if (mode === 'disabled' || reason !== undefined)
                return undefined;
        }
        else {
            if (reason === undefined)
                return undefined;
            if ((mode === 'disabled') !== (reason === 'disabled'))
                return undefined;
        }
        return {
            id,
            mode: mode,
            available,
            ...(reason === undefined ? {} : { reason: reason }),
        };
    }
    return undefined;
}
export function readReadyExternalIntegrationStatus(value, id, overallReady) {
    const status = readExternalIntegrationStatus(value, id);
    if (!status?.available)
        return status;
    return overallReady ? status : undefined;
}
//# sourceMappingURL=external-integrations.js.map